// Reads the MigrationBuilder operations in a migration's Up() and Down() methods.
// They give rename hints (RenameColumn/RenameTable), the "database operations" view of a migration,
// and flag hand-written code (raw SQL, conditionals) that the model snapshots cannot show.
import type { Arg, Expr, LambdaExpr, Span, Stmt } from '../csharp/ast';
import type { Diagnostic } from '../csharp/diagnostics';
import { analyzeEfFile, findMethod, type EfFileInfo } from './efFile';
import { evalValue, toCanonical, typeDisplayName, type JsonValue } from './values';

export interface ColumnOperation {
  name: string;
  clrType: string | null;
  args: Record<string, JsonValue>;
  annotations: Record<string, JsonValue>;
  span: Span;
}

export interface ConstraintOperation {
  kind: string;
  name: string | null;
  columns: string[];
  args: Record<string, JsonValue>;
  span: Span;
}

export interface MigrationOperation {
  /** MigrationBuilder method: CreateTable, AddColumn, RenameColumn, Sql, ... */
  kind: string;
  /** Generic type argument, e.g. `int` for AddColumn<int>. */
  clrType: string | null;
  /** Named arguments (positional ones get their conventional names). Non-constant values keep their source text as `{ expr }`. */
  args: Record<string, JsonValue>;
  annotations: Record<string, JsonValue>;
  oldAnnotations: Record<string, JsonValue>;
  columns?: ColumnOperation[];
  constraints?: ConstraintOperation[];
  /** True when the call sits inside hand-written control flow (if, loops). */
  conditional: boolean;
  span: Span;
}

export interface CustomCode {
  direction: 'up' | 'down';
  text: string;
  reason: string;
  span: Span;
}

export interface OperationsReadResult {
  info: EfFileInfo;
  up: MigrationOperation[];
  down: MigrationOperation[];
  customCode: CustomCode[];
  diagnostics: Diagnostic[];
}

const POSITIONAL: Record<string, string[]> = {
  Sql: ['sql', 'suppressTransaction'],
  PrimaryKey: ['name', 'columns'],
  UniqueConstraint: ['name', 'columns'],
  CheckConstraint: ['name', 'sql'],
  Annotation: ['name', 'value'],
  OldAnnotation: ['name', 'value'],
  EnsureSchema: ['name'],
  DropTable: ['name', 'schema'],
};

/** Reads Up() and Down() from a migration's main `.cs` file. */
export function readOperations(text: string): OperationsReadResult {
  const info = analyzeEfFile(text);
  const customCode: CustomCode[] = [];
  const read = (direction: 'up' | 'down', methodName: string): MigrationOperation[] => {
    const method = findMethod(info.cls, methodName);
    if (!method?.body) return [];
    const builder = method.params[0]?.name ?? 'migrationBuilder';
    const ops: MigrationOperation[] = [];
    new OperationReader(text, builder, direction, ops, customCode).walk(method.body, false);
    return ops;
  };
  const up = read('up', 'Up');
  const down = read('down', 'Down');
  return { info, up, down, customCode, diagnostics: info.diagnostics };
}

class OperationReader {
  constructor(
    private readonly source: string,
    private readonly builder: string,
    private readonly direction: 'up' | 'down',
    private readonly ops: MigrationOperation[],
    private readonly custom: CustomCode[],
  ) {}

  walk(stmts: Stmt[], conditional: boolean): void {
    for (const stmt of stmts) {
      switch (stmt.kind) {
        case 'expr': {
          const op = this.operation(stmt.expr, conditional);
          if (op) this.ops.push(op);
          else this.note(stmt.span, 'code');
          break;
        }
        case 'block':
          this.walk(stmt.body, conditional);
          break;
        case 'if':
          this.note({ start: stmt.span.start, end: stmt.test.span.end + 1 }, 'if');
          this.walk([stmt.then], true);
          if (stmt.else) this.walk([stmt.else], true);
          break;
        case 'loop':
          this.note(stmt.span, stmt.keyword);
          this.walk([stmt.body], true);
          break;
        case 'local':
          this.note(stmt.span, 'variable');
          break;
        case 'unknown':
          this.note(stmt.span, stmt.reason);
          break;
        case 'try':
        case 'switch':
        case 'throw':
        case 'yield':
          this.note(stmt.span, stmt.kind);
          break;
        case 'guarded':
          this.note(stmt.span, stmt.keyword);
          break;
        case 'localFunction':
          this.note(stmt.span, 'local function');
          break;
        case 'empty':
        case 'return':
          break;
      }
    }
  }

  private note(span: Span, reason: string): void {
    this.custom.push({ direction: this.direction, text: this.source.slice(span.start, span.end), reason, span });
  }

  private operation(expr: Expr, conditional: boolean): MigrationOperation | null {
    const annotations: Record<string, JsonValue> = {};
    const oldAnnotations: Record<string, JsonValue> = {};
    const core = this.unwrapAnnotations(expr, annotations, oldAnnotations);
    if (core.kind !== 'call' || core.callee.kind !== 'member') return null;
    const target = core.callee.target;
    if (target.kind !== 'name' || target.name !== this.builder) return null;
    const kind = core.callee.name;
    const op: MigrationOperation = {
      kind,
      clrType: core.callee.typeArgs?.[0] ? typeDisplayName(core.callee.typeArgs[0]) : null,
      args: {},
      annotations,
      oldAnnotations,
      conditional,
      span: expr.span,
    };
    const positional = POSITIONAL[kind] ?? [];
    core.args.forEach((arg, i) => {
      const name = arg.name ?? positional[i] ?? `$${i}`;
      if (kind === 'CreateTable' && name === 'columns' && arg.value.kind === 'lambda') {
        op.columns = this.columns(arg.value);
      } else if (kind === 'CreateTable' && name === 'constraints' && arg.value.kind === 'lambda') {
        op.constraints = this.constraints(arg.value);
      } else {
        op.args[name] = this.value(arg);
      }
    });
    return op;
  }

  /** Strips chained `.Annotation(...)` / `.OldAnnotation(...)` calls, collecting them. */
  private unwrapAnnotations(expr: Expr, annotations: Record<string, JsonValue>, oldAnnotations: Record<string, JsonValue>): Expr {
    let current = expr;
    while (current.kind === 'call' && current.callee.kind === 'member' && (current.callee.name === 'Annotation' || current.callee.name === 'OldAnnotation')) {
      const [nameArg, valueArg] = current.args;
      const name = nameArg ? evalValue(nameArg.value, this.source) : null;
      if (name && name.t === 'string' && valueArg) {
        const into = current.callee.name === 'Annotation' ? annotations : oldAnnotations;
        into[name.v] = this.value(valueArg);
      }
      current = current.callee.target;
    }
    return current;
  }

  private columns(lambda: LambdaExpr): ColumnOperation[] {
    const table = lambda.params[0]?.name;
    const body = lambda.body;
    if (body.kind !== 'anonymous' || !table) return [];
    const out: ColumnOperation[] = [];
    for (const member of body.members) {
      const annotations: Record<string, JsonValue> = {};
      const core = this.unwrapAnnotations(member.value, annotations, {});
      if (core.kind !== 'call' || core.callee.kind !== 'member' || core.callee.name !== 'Column') continue;
      const args: Record<string, JsonValue> = {};
      core.args.forEach((arg, i) => {
        args[arg.name ?? `$${i}`] = this.value(arg);
      });
      const explicitName = typeof args['name'] === 'string' ? (args['name'] as string) : null;
      out.push({
        name: explicitName ?? member.name ?? '?',
        clrType: core.callee.typeArgs?.[0] ? typeDisplayName(core.callee.typeArgs[0]) : null,
        args,
        annotations,
        span: member.value.span,
      });
    }
    return out;
  }

  private constraints(lambda: LambdaExpr): ConstraintOperation[] {
    const table = lambda.params[0]?.name;
    const stmts = lambda.body.kind === 'block' ? lambda.body.body : [{ kind: 'expr' as const, expr: lambda.body, span: lambda.body.span }];
    const out: ConstraintOperation[] = [];
    for (const stmt of stmts) {
      if (stmt.kind !== 'expr') continue;
      const core = this.unwrapAnnotations(stmt.expr, {}, {});
      if (core.kind !== 'call' || core.callee.kind !== 'member') continue;
      if (core.callee.target.kind !== 'name' || core.callee.target.name !== table) continue;
      const kind = core.callee.name;
      const positional = POSITIONAL[kind] ?? [];
      const args: Record<string, JsonValue> = {};
      let columns: string[] = [];
      core.args.forEach((arg, i) => {
        const name = arg.name ?? positional[i] ?? `$${i}`;
        if ((name === 'columns' || name === 'column') && arg.value.kind === 'lambda') columns = selectedMembers(arg.value);
        else args[name] = this.value(arg);
      });
      out.push({ kind, name: typeof args['name'] === 'string' ? (args['name'] as string) : null, columns, args, span: stmt.span });
    }
    return out;
  }

  private value(arg: Arg): JsonValue {
    const v = toCanonical(evalValue(arg.value, this.source));
    return v === undefined ? { expr: this.source.slice(arg.value.span.start, arg.value.span.end) } : v;
  }
}

/** `x => x.Id` gives ["Id"]; `x => new { x.A, x.B }` gives ["A", "B"]. */
function selectedMembers(lambda: LambdaExpr): string[] {
  const body = lambda.body;
  if (body.kind === 'member') return [body.name];
  if (body.kind === 'anonymous') return body.members.map((m) => m.name).filter((n): n is string => n !== null);
  return [];
}

/** Rename hints from a migration: old and new names of tables and columns. */
export interface RenameHint {
  kind: 'table' | 'column' | 'index';
  table: string | null;
  schema: string | null;
  from: string;
  to: string;
}

export function renameHints(ops: MigrationOperation[]): RenameHint[] {
  const hints: RenameHint[] = [];
  const s = (v: JsonValue | undefined) => (typeof v === 'string' ? v : null);
  for (const op of ops) {
    const name = s(op.args['name']);
    const newName = s(op.args['newName']);
    if (!name || !newName) continue;
    if (op.kind === 'RenameTable') hints.push({ kind: 'table', table: name, schema: s(op.args['schema']), from: name, to: newName });
    if (op.kind === 'RenameColumn') hints.push({ kind: 'column', table: s(op.args['table']), schema: s(op.args['schema']), from: name, to: newName });
    if (op.kind === 'RenameIndex') hints.push({ kind: 'index', table: s(op.args['table']), schema: s(op.args['schema']), from: name, to: newName });
  }
  return hints;
}
